import { useEffect, useMemo, useState } from 'react';
import {
  attendanceTemplateFiles,
  attendanceUserFallbackTemplates,
  DEFAULT_ATTENDANCE_TEMPLATE_FILE
} from '../../constants/dashboardData';
import { createAirtableRecord } from '../../services/airtable';
import attendanceImageManifest from '../../constants/attendanceImageManifest.json';
import defaultAttendanceTemplateBundleUrl from '../../assets/attendance-templates/Formato Asistencia General.xlsx';

const STATUS_OPTIONS = ['all', 'Abierta', 'Cerrada', 'Incompleta'];
const VALIDATION_OPTIONS = ['all', 'Válida', 'Duplicado', 'Fuera de horario', 'Token inválido', 'Usuario inactivo'];
const METHOD_OPTIONS = ['all', 'QR', 'Manual', 'Importado'];
const TYPE_OPTIONS = ['Día Festivo', 'Descanso', 'Home-Office', 'Permiso Falta', 'Enfermedad', 'Vacaciones', 'Asistencia'];
const EXCELJS_CDN_URL = 'https://cdn.jsdelivr.net/npm/exceljs@4.4.0/dist/exceljs.min.js';
const JSZIP_CDN_URL = 'https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js';
const PUBLIC_ASSET_BASE = process.env.PUBLIC_URL || '';
const ATTENDANCE_IMAGES_BASE = `${PUBLIC_ASSET_BASE}/img-asistencias`;
const BUNDLED_ATTENDANCE_TEMPLATE_BY_FILE = {
  [DEFAULT_ATTENDANCE_TEMPLATE_FILE]: defaultAttendanceTemplateBundleUrl
};
const DEBUG_LOG_ENABLED = process.env.NODE_ENV !== 'production';
const ASISTENCIAS_FIELD_IDS = {
  usuario: 'fldSD0L5M8Nc9adrF',
  fecha: 'fldtcFOYbFzi8sPoq',
  tipo: 'fldIt2bS7sorKYlZ7'
};
const DEFAULT_USER_SIGNATURE_IMAGE_BY_KEY = {
  'jc-j002': 'jare.PNG',
  'jaretzy-camarillo': 'jare.PNG',
  'ma-j003': 'madai.PNG',
  'madai-aragon': 'madai.PNG',
  'av-j001': 'lili.PNG',
  'arleth-chavez': 'lili.PNG',
  'lilian-arleth-villalpando-chavez': 'lili.PNG',
  'mr-j005': 'moises.PNG',
  'moises-rivera': 'moises.PNG',
  'ac-j004': 'angel.PNG',
  'angel-chavarria': 'angel.PNG',
  'ag-j006': 'anahi.PNG',
  'anahi-gonzalez': 'anahi.PNG',
  'ar-je003': 'alex.PNG',
  'alejandro-gonzalez': 'alex.PNG',
  'lc-je002': 'liz.PNG',
  'lizbeth-padilla': 'liz.PNG',
  'ez-je001': 'eli.PNG',
  'eli-zapien': 'eli.PNG',
  'luis-eli-zapien': 'eli.PNG'
};
const DEFAULT_CENTER_IMAGE_BY_CENTER_KEY = {
  'cuu-ac-servicios-2020-sc': 'sergio.PNG',
  'luz-elena-najera-sanchez': 'luz-elena.PNG',
  'argelia-fernandez-de-haro': 'argelia.PNG'
};
const DEFAULT_CENTER_IMAGE_BY_TUTOR_KEY = {
  'sergio-alejandro-chavarria-solis': 'sergio.PNG',
  'luz-elena-najera-sanchez': 'luz-elena.PNG',
  'argelia-fernandez-de-haro': 'argelia.PNG'
};
const IMAGE_MANIFEST_ALIASES = attendanceImageManifest?.aliases || {};
const ATTENDANCE_IMAGE_META_BY_FILE = attendanceImageManifest?.images || {};
const USER_SIGNATURE_IMAGE_BY_KEY = {
  ...DEFAULT_USER_SIGNATURE_IMAGE_BY_KEY,
  ...(IMAGE_MANIFEST_ALIASES.users || {})
};
const CENTER_IMAGE_BY_CENTER_KEY = {
  ...DEFAULT_CENTER_IMAGE_BY_CENTER_KEY,
  ...(IMAGE_MANIFEST_ALIASES.centerByCenterName || {})
};
const CENTER_IMAGE_BY_TUTOR_KEY = {
  ...DEFAULT_CENTER_IMAGE_BY_TUTOR_KEY,
  ...(IMAGE_MANIFEST_ALIASES.centerByTutorName || {})
};

let excelJsLoader = null;
let jsZipLoader = null;

function logDevXlsx(message, details) {
  if (!DEBUG_LOG_ENABLED) return;
  if (details === undefined) {
    console.info(`[XLSX] ${message}`);
    return;
  }
  console.info(`[XLSX] ${message}`, details);
}
const attendanceImageAssetCache = new Map();

async function sanitizeTemplateBuffer(buffer) {
  const JSZip = await loadJsZipLibrary();
  const zip = await JSZip.loadAsync(buffer);

  const updateXml = async (path, transform) => {
    const file = zip.file(path);
    if (!file) return;
    const content = await file.async('string');
    const next = transform(content);
    if (next !== content) {
      zip.file(path, next);
    }
  };

  await updateXml('xl/workbook.xml', content =>
    content.replace(/<definedNames>[\s\S]*?<\/definedNames>/g, '')
  );

  const sanitizeWorksheetXml = content => {
    let next = content
      .replace(/<[^>]*headerFooter[^>]*>[\s\S]*?<\/[^>]*headerFooter>/g, '')
      .replace(/<[^>]*legacyDrawingHF[^>]*\/>/g, '')
      .replace(/<[^>]*legacyDrawing[^>]*\/>/g, '');

    let prefix = null;
    next = next.replace(/<([a-zA-Z0-9]+):worksheet([^>]*)>/, (match, foundPrefix, attrs) => {
      prefix = foundPrefix;
      const cleaned = attrs
        .replace(/\sxmlns:[^=]+="[^"]*"/g, '')
        .replace(/\s[^=]*:Ignorable="[^"]*"/g, '')
        .replace(/\s[^=]*:uid="[^"]*"/g, '');
      return `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"${cleaned}>`;
    });
    if (prefix) {
      const closeRe = new RegExp(`</${prefix}:`, 'g');
      const openRe = new RegExp(`<${prefix}:`, 'g');
      next = next.replace(closeRe, '</').replace(openRe, '<');
    }

    next = next.replace(/<c([^>]*?)t="s"([^>]*)>([\s\S]*?)<\/c>/g, (match, pre, post, inner) => {
      if (!/<is\b/.test(inner)) return match;
      const cleaned = inner.replace(/<v>[\s\S]*?<\/v>/g, '');
      return `<c${pre}t="inlineStr"${post}>${cleaned}</c>`;
    });

    next = next.replace(/<c([^>]*?)t="inlineStr"([^>]*)>([\s\S]*?)<\/c>/g, (match, pre, post, inner) => {
      if (!/<v>/.test(inner)) return match;
      const cleaned = inner.replace(/<v>[\s\S]*?<\/v>/g, '');
      return `<c${pre}t="inlineStr"${post}>${cleaned}</c>`;
    });

    return next;
  };

  const sanitizeSharedStringsXml = content => {
    let next = content;
    let prefix = null;
    next = next.replace(/<([a-zA-Z0-9]+):sst([^>]*)>/, (match, foundPrefix, attrs) => {
      prefix = foundPrefix;
      const cleaned = attrs.replace(/\sxmlns:[^=]+="[^"]*"/g, '');
      return `<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"${cleaned}>`;
    });
    if (prefix) {
      const closeRe = new RegExp(`</${prefix}:`, 'g');
      const openRe = new RegExp(`<${prefix}:`, 'g');
      next = next.replace(closeRe, '</').replace(openRe, '<');
    }
    return next;
  };

  const worksheetPaths = Object.keys(zip.files).filter(name =>
    /^xl\/worksheets\/sheet\d+\.xml$/.test(name)
  );
  for (const sheetPath of worksheetPaths) {
    await updateXml(sheetPath, sanitizeWorksheetXml);
  }

  await updateXml('xl/sharedStrings.xml', sanitizeSharedStringsXml);

  await updateXml('xl/worksheets/_rels/sheet1.xml.rels', content =>
    content.replace(
      /<Relationship[^>]+Type="[^"]*(vmlDrawing|printerSettings)[^"]*"[^>]*\/>/g,
      ''
    )
  );

  Object.keys(zip.files).forEach(name => {
    if (name.includes('vmlDrawing') || name.includes('printerSettings')) {
      zip.remove(name);
    }
  });

  return zip.generateAsync({ type: 'arraybuffer' });
}

async function loadTemplateWorkbook(buffer) {
  const ExcelJS = await loadExcelJsLibrary();
  const workbook = new ExcelJS.Workbook();
  try {
    await workbook.xlsx.load(buffer);
    return workbook;
  } catch (error) {
    console.warn('[XLSX] load failed, sanitizing template', error);
    const sanitized = await sanitizeTemplateBuffer(buffer);
    const retry = new ExcelJS.Workbook();
    await retry.xlsx.load(sanitized);
    logDevXlsx('Workbook sanitizado y recargado', {
      sheetCount: retry.worksheets?.length || 0,
      sheetNames: retry.worksheets?.map(sheet => sheet?.name)
    });
    return retry;
  }
}

function loadExcelJsLibrary() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('La exportación XLSX solo está disponible en el navegador.'));
  }
  if (window.ExcelJS) return Promise.resolve(window.ExcelJS);
  if (!excelJsLoader) {
    excelJsLoader = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = EXCELJS_CDN_URL;
      script.async = true;
      script.onload = () => {
        if (window.ExcelJS) {
          resolve(window.ExcelJS);
          return;
        }
        reject(new Error('No fue posible inicializar la libreria XLSX.'));
      };
      script.onerror = () => reject(new Error('No se pudo cargar la libreria XLSX.'));
      document.head.appendChild(script);
    });
  }
  return excelJsLoader;
}

function loadJsZipLibrary() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('La exportación ZIP solo está disponible en el navegador.'));
  }
  if (window.JSZip) return Promise.resolve(window.JSZip);
  if (!jsZipLoader) {
    jsZipLoader = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = JSZIP_CDN_URL;
      script.async = true;
      script.onload = () => {
        if (window.JSZip) {
          resolve(window.JSZip);
          return;
        }
        reject(new Error('No fue posible inicializar la libreria ZIP.'));
      };
      script.onerror = () => reject(new Error('No se pudo cargar la libreria ZIP.'));
      document.head.appendChild(script);
    });
  }
  return jsZipLoader;
}

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDateAtMidday(dateText) {
  if (!dateText) return null;
  const date = new Date(`${dateText}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function buildDateRange(startDate, endDate) {
  if (!startDate || !endDate) return [];
  const start = parseDateAtMidday(startDate);
  const end = parseDateAtMidday(endDate);
  if (!start || !end || start > end) return [];
  const dates = [];
  const cursor = new Date(start.getTime());
  while (cursor <= end) {
    dates.push(toDateInputValue(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

function getDateFromValue(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return toDateInputValue(date);
}

function getRecordDate(record) {
  if (!record) return '';
  return record.fecha || getDateFromValue(record.inicio) || getDateFromValue(record.fin) || '';
}

function formatDate(value) {
  if (!value) return 'Sin fecha';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' }).format(date);
}

function formatTime(value) {
  if (!value) return '---';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '---';
  return new Intl.DateTimeFormat('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(date);
}

function formatDuration(durationMin, startValue, endValue) {
  if (Number.isFinite(durationMin)) {
    const hours = Math.floor(durationMin / 60);
    const minutes = durationMin % 60;
    if (!hours) return `${minutes} min`;
    return minutes ? `${hours} h ${minutes} min` : `${hours} h`;
  }
  if (!startValue || !endValue) return '---';
  const start = new Date(startValue);
  const end = new Date(endValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '---';
  const minutes = Math.max(Math.round((end.getTime() - start.getTime()) / 60000), 0);
  return formatDuration(minutes);
}

function toClassName(value) {
  const normalized = String(value || '')
    .normalize ? String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '') : String(value || '');
  return normalized
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function buildAttendanceImageUrl(fileName) {
  if (!fileName) return '';
  return `${ATTENDANCE_IMAGES_BASE}/${encodeURIComponent(fileName)}`;
}

function resolveUserSignatureImage(user) {
  const lookupKeys = [
    toClassName(user?.clave),
    toClassName(user?.nombre),
    toClassName(user?.correo)
  ].filter(Boolean);

  for (const key of lookupKeys) {
    const fileName = USER_SIGNATURE_IMAGE_BY_KEY[key];
    if (fileName) return fileName;
  }
  return '';
}

function resolveCenterSignatureImage(user) {
  const centerKey = toClassName(user?.nombreCentroPatron);
  if (centerKey && CENTER_IMAGE_BY_CENTER_KEY[centerKey]) {
    return CENTER_IMAGE_BY_CENTER_KEY[centerKey];
  }
  const tutorKey = toClassName(user?.tutor);
  if (tutorKey && CENTER_IMAGE_BY_TUTOR_KEY[tutorKey]) {
    return CENTER_IMAGE_BY_TUTOR_KEY[tutorKey];
  }
  return '';
}

function isActiveUser(user) {
  if (!user) return false;
  if (typeof user.activo === 'boolean') return user.activo;
  const normalized = toClassName(user.activo);
  return normalized === 'si' || normalized === 'true' || normalized === 'activo';
}

function hasQr(user) {
  if (!user) return false;
  if (Array.isArray(user.qr)) return user.qr.length > 0;
  if (typeof user.qr === 'string') return user.qr.trim().length > 0;
  return false;
}

function splitNameParts(value) {
  const fullName = String(value || '').trim();
  if (!fullName) return { nombre: '', apellido: '' };
  const parts = fullName.split(/\s+/);
  if (parts.length === 1) return { nombre: parts[0], apellido: '' };
  return {
    nombre: parts.slice(0, -1).join(' '),
    apellido: parts[parts.length - 1]
  };
}

function formatDateForTemplate(value) {
  if (!value) return '';
  const date = value.includes('T') ? new Date(value) : new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function formatTimeForTemplate(value) {
  if (!value) return 'NA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'NA';
  const rawHours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const suffix = rawHours >= 12 ? 'pm' : 'am';
  const hours = rawHours % 12 || 12;
  return `${hours}:${minutes}${suffix}`;
}

function buildTemplateRows(records, userById) {
  const sorted = [...records].sort((a, b) => {
    const left = `${getRecordDate(a)}${a.inicio || ''}`;
    const right = `${getRecordDate(b)}${b.inicio || ''}`;
    if (left < right) return -1;
    if (left > right) return 1;
    return 0;
  });

  const rows = [];
  sorted.forEach(record => {
    const user = userById[record.usuarioId] || {};
    const parsedName = splitNameParts(user.nombre);
    const incident = record.validacion && toClassName(record.validacion) !== 'valida' ? record.validacion : '';
    const dateText = formatDateForTemplate(getRecordDate(record));
    const selfieAttachment = Array.isArray(record.selfie) ? record.selfie[0] : null;
    const isNonAttendance = record.tipo && toClassName(record.tipo) !== 'asistencia';
    const common = [
      user.clave || user.iniciales || '',
      parsedName.nombre || user.nombre || '',
      parsedName.apellido || '',
      dateText
    ];

    if (isNonAttendance) {
      const timeValue = record.inicio || record.fin;
      rows.push({
        values: [
          ...common,
          timeValue ? formatTimeForTemplate(timeValue) : 'NA',
          record.tipo,
          incident || 'Sin hora registrada',
          'Matriz',
          null,
          null,
          null
        ],
        selfieAttachment
      });
      return;
    }

    if (record.inicio) {
      rows.push({
        values: [
          ...common,
          formatTimeForTemplate(record.inicio),
          isNonAttendance ? record.tipo : 'Inicio de Labores',
          incident,
          'Matriz',
          null,
          null,
          null
        ],
        selfieAttachment
      });
    }
    if (record.fin) {
      rows.push({
        values: [
          ...common,
          formatTimeForTemplate(record.fin),
          isNonAttendance ? record.tipo : 'Fin de Labores',
          incident,
          'Matriz',
          null,
          null,
          null
        ],
        selfieAttachment
      });
    }
    if (!record.inicio && !record.fin) {
      rows.push({
        values: [
          ...common,
          'NA',
          record.tipo || record.estado || 'Registro',
          incident || 'Sin hora registrada',
          'Matriz',
          null,
          null,
          null
        ],
        selfieAttachment
      });
    }
  });

  return rows;
}

function sanitizeFilename(value) {
  return String(value || '').trim().replace(/[^a-z0-9._-]+/gi, '_');
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function normalizePublicAssetBase(basePath) {
  const normalized = String(basePath || '').trim();
  if (!normalized || normalized === '/') return '';
  return normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
}

function buildTemplateUrls(fileName) {
  const encodedFileName = encodeURIComponent(fileName);
  const candidates = [];
  const seen = new Set();
  const addExactCandidate = url => {
    const normalizedUrl = String(url || '').trim();
    if (!normalizedUrl || seen.has(normalizedUrl)) return;
    seen.add(normalizedUrl);
    candidates.push(normalizedUrl);
  };
  const addCandidate = basePath => {
    const normalizedBase = normalizePublicAssetBase(basePath);
    const url = `${normalizedBase}/asistencias-template/${encodedFileName}`;
    addExactCandidate(url);
  };

  addCandidate(PUBLIC_ASSET_BASE);
  addCandidate('');
  addExactCandidate(BUNDLED_ATTENDANCE_TEMPLATE_BY_FILE[fileName]);
  return candidates;
}

function getSelfieUrl(attachment) {
  if (!attachment) return '';
  if (attachment.thumbnails?.small?.url) return attachment.thumbnails.small.url;
  return attachment.url || '';
}

function getImageExtension(attachment, url) {
  const type = attachment?.type || '';
  if (type.includes('png')) return 'png';
  if (type.includes('jpg') || type.includes('jpeg')) return 'jpeg';
  if (url) {
    const lower = url.toLowerCase();
    if (lower.includes('.png')) return 'png';
    if (lower.includes('.jpg') || lower.includes('.jpeg')) return 'jpeg';
  }
  return 'jpeg';
}

function blobToDataUrl(blob) {
  if (typeof FileReader === 'undefined') {
    return Promise.reject(new Error('No se pudo convertir una imagen.'));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('No se pudo convertir una imagen.'));
    reader.readAsDataURL(blob);
  });
}

async function fetchImageBase64(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('No se pudo descargar la selfie.');
  }
  const blob = await response.blob();
  return blobToDataUrl(blob);
}

function loadImageElement(dataUrl) {
  if (typeof Image === 'undefined') {
    return Promise.reject(new Error('No se pudo cargar la imagen.'));
  }
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('No se pudo cargar la imagen.'));
    image.src = dataUrl;
  });
}

async function trimTransparentPadding(base64DataUrl) {
  const image = await loadImageElement(base64DataUrl);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  if (!width || !height || typeof document === 'undefined') {
    return { base64: base64DataUrl, width, height, trimmed: false };
  }

  const sourceCanvas = document.createElement('canvas');
  sourceCanvas.width = width;
  sourceCanvas.height = height;
  const sourceContext = sourceCanvas.getContext('2d', { willReadFrequently: true });
  if (!sourceContext) {
    return { base64: base64DataUrl, width, height, trimmed: false };
  }
  sourceContext.drawImage(image, 0, 0);
  const pixels = sourceContext.getImageData(0, 0, width, height).data;

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const alpha = pixels[(y * width + x) * 4 + 3];
      if (alpha <= 12) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }

  if (maxX < minX || maxY < minY) {
    return { base64: base64DataUrl, width, height, trimmed: false };
  }

  const cropPad = 2;
  const left = Math.max(0, minX - cropPad);
  const top = Math.max(0, minY - cropPad);
  const right = Math.min(width - 1, maxX + cropPad);
  const bottom = Math.min(height - 1, maxY + cropPad);
  const cropWidth = right - left + 1;
  const cropHeight = bottom - top + 1;

  const almostFullWidth = cropWidth >= width - 2;
  const almostFullHeight = cropHeight >= height - 2;
  if (almostFullWidth && almostFullHeight) {
    return { base64: base64DataUrl, width, height, trimmed: false };
  }

  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = cropWidth;
  cropCanvas.height = cropHeight;
  const cropContext = cropCanvas.getContext('2d');
  if (!cropContext) {
    return { base64: base64DataUrl, width, height, trimmed: false };
  }
  cropContext.drawImage(sourceCanvas, left, top, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
  return {
    base64: cropCanvas.toDataURL('image/png'),
    width: cropWidth,
    height: cropHeight,
    trimmed: true
  };
}

async function fetchWorkbookImage(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('No se pudo descargar una imagen de asistencia.');
  }
  const blob = await response.blob();
  const base64 = await blobToDataUrl(blob);
  const trimmed = await trimTransparentPadding(base64);
  return {
    base64: trimmed.base64,
    width: trimmed.width,
    height: trimmed.height,
    extension: trimmed.trimmed ? 'png' : getImageExtension({ type: blob.type }, url)
  };
}

function getImageAspectProfile(width, height) {
  if (!width || !height) return 'landscape';
  const ratio = width / height;
  if (ratio >= 2.2) return 'wide';
  if (ratio >= 1.2) return 'landscape';
  if (ratio >= 0.82) return 'square';
  return 'portrait';
}

function getImageManifestPlacement(fileName, placementKey) {
  if (!fileName) return null;
  const imageMeta = ATTENDANCE_IMAGE_META_BY_FILE[fileName];
  if (!imageMeta || !imageMeta.placement) return null;
  return imageMeta.placement[placementKey] || null;
}

function getUserHeaderImageRowHeight(imageProfile, fileName) {
  const manifestPlacement = getImageManifestPlacement(fileName, 'userHeader');
  if (manifestPlacement?.rowHeight) return manifestPlacement.rowHeight;
  if (imageProfile === 'portrait') return 72;
  if (imageProfile === 'square') return 64;
  if (imageProfile === 'landscape') return 56;
  return 52;
}

function getUserHeaderImageLimits(imageProfile, fileName) {
  const manifestPlacement = getImageManifestPlacement(fileName, 'userHeader');
  if (manifestPlacement) {
    return {
      maxWidth: manifestPlacement.maxWidth,
      maxHeight: manifestPlacement.maxHeight,
      paddingX: manifestPlacement.paddingX,
      paddingY: manifestPlacement.paddingY
    };
  }
  if (imageProfile === 'wide') return { maxWidth: 244, maxHeight: 56, paddingX: 6, paddingY: 2 };
  if (imageProfile === 'landscape') return { maxWidth: 216, maxHeight: 58, paddingX: 6, paddingY: 2 };
  if (imageProfile === 'square') return { maxWidth: 120, maxHeight: 62, paddingX: 4, paddingY: 2 };
  return { maxWidth: 92, maxHeight: 88, paddingX: 4, paddingY: 2 };
}

function getCenterImageLimits(imageProfile, fileName) {
  const manifestPlacement = getImageManifestPlacement(fileName, 'centerHeader');
  if (manifestPlacement) {
    return {
      maxWidth: manifestPlacement.maxWidth,
      maxHeight: manifestPlacement.maxHeight,
      paddingX: manifestPlacement.paddingX,
      paddingY: manifestPlacement.paddingY
    };
  }
  if (imageProfile === 'wide') return { maxWidth: 168, maxHeight: 86, paddingX: 4, paddingY: 4 };
  if (imageProfile === 'landscape') return { maxWidth: 160, maxHeight: 114, paddingX: 4, paddingY: 4 };
  if (imageProfile === 'square') return { maxWidth: 136, maxHeight: 132, paddingX: 4, paddingY: 4 };
  return { maxWidth: 112, maxHeight: 146, paddingX: 4, paddingY: 4 };
}

function fitImageToBox(width, height, maxWidth, maxHeight) {
  const sourceWidth = Math.max(Number(width) || 1, 1);
  const sourceHeight = Math.max(Number(height) || 1, 1);
  const targetWidth = Math.max(Number(maxWidth) || 1, 1);
  const targetHeight = Math.max(Number(maxHeight) || 1, 1);
  const scale = Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight);
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale))
  };
}

function columnWidthToPixels(width) {
  const normalized = Number.isFinite(width) ? width : 8.43;
  return Math.max(8, Math.round(normalized * 7 + 5));
}

function rowHeightToPixels(height) {
  const normalized = Number.isFinite(height) ? height : 15;
  return Math.max(12, Math.round(normalized * (4 / 3)));
}

function getColumnPixels(worksheet, colNumber) {
  const column = worksheet.getColumn(colNumber);
  const defaultWidth = Number.isFinite(worksheet?.properties?.defaultColWidth)
    ? worksheet.properties.defaultColWidth
    : 8.43;
  return columnWidthToPixels(Number.isFinite(column?.width) ? column.width : defaultWidth);
}

function getRowPixels(worksheet, rowNumber) {
  const row = worksheet.getRow(rowNumber);
  const defaultHeight = Number.isFinite(worksheet?.properties?.defaultRowHeight)
    ? worksheet.properties.defaultRowHeight
    : 15;
  return rowHeightToPixels(Number.isFinite(row?.height) ? row.height : defaultHeight);
}

function getRangePixels(worksheet, startCol, endCol, startRow, endRow) {
  let width = 0;
  let height = 0;
  for (let col = startCol; col <= endCol; col += 1) {
    width += getColumnPixels(worksheet, col);
  }
  for (let row = startRow; row <= endRow; row += 1) {
    height += getRowPixels(worksheet, row);
  }
  return { width, height };
}

function pixelsToColumnOffset(worksheet, startCol, pixels) {
  let remaining = Math.max(0, pixels);
  let column = startCol;
  let offset = 0;
  while (remaining > 0 && column < startCol + 64) {
    const columnPixels = getColumnPixels(worksheet, column);
    if (remaining >= columnPixels) {
      offset += 1;
      remaining -= columnPixels;
      column += 1;
      continue;
    }
    offset += remaining / columnPixels;
    remaining = 0;
  }
  return offset;
}

function pixelsToRowOffset(worksheet, startRow, pixels) {
  let remaining = Math.max(0, pixels);
  let row = startRow;
  let offset = 0;
  while (remaining > 0 && row < startRow + 64) {
    const rowPixels = getRowPixels(worksheet, row);
    if (remaining >= rowPixels) {
      offset += 1;
      remaining -= rowPixels;
      row += 1;
      continue;
    }
    offset += remaining / rowPixels;
    remaining = 0;
  }
  return offset;
}

function placeImageInRange({
  worksheet,
  imageId,
  image,
  startCol,
  endCol,
  startRow,
  endRow,
  maxWidth,
  maxHeight,
  paddingX = 4,
  paddingY = 2
}) {
  if (!worksheet || !imageId || !image?.width || !image?.height) return;
  const range = getRangePixels(worksheet, startCol, endCol, startRow, endRow);
  if (!range.width || !range.height) return;

  const boxWidth = Math.max(1, Math.min(range.width, maxWidth || range.width));
  const boxHeight = Math.max(1, Math.min(range.height, maxHeight || range.height));
  const innerWidth = Math.max(1, boxWidth - paddingX * 2);
  const innerHeight = Math.max(1, boxHeight - paddingY * 2);
  const fitted = fitImageToBox(image.width, image.height, innerWidth, innerHeight);

  const offsetX = (range.width - boxWidth) / 2 + paddingX + Math.max(0, (innerWidth - fitted.width) / 2);
  const offsetY = (range.height - boxHeight) / 2 + paddingY + Math.max(0, (innerHeight - fitted.height) / 2);

  worksheet.addImage(imageId, {
    tl: {
      col: startCol - 1 + pixelsToColumnOffset(worksheet, startCol, offsetX),
      row: startRow - 1 + pixelsToRowOffset(worksheet, startRow, offsetY)
    },
    ext: fitted
  });
}

function applyRowValues(row, values) {
  for (let col = 1; col <= 11; col += 1) {
    const rawValue = values[col - 1];
    const normalized = rawValue === '' ? null : rawValue;
    row.getCell(col).value = normalized === undefined ? null : normalized;
  }
}

const PROGRAM_TITLE = 'Programa Jovenes Construyendo el Futuro';

function normalizeUserValue(value) {
  if (value === undefined || value === null) return '';
  const text = String(value).trim();
  if (!text) return '';
  const normalized = text.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (normalized === 'na') return '';
  return text;
}

async function downloadWorkbook(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer({ useStyles: true, useSharedStrings: true });
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  downloadBlob(blob, filename);
}

function AttendanceReportsBoard({
  records = [],
  users = [],
  templates = [],
  userTemplates = [],
  defaultTemplateFile = '',
  asistenciasTableId,
  onRefresh,
  loading = false,
  error = '',
  usingLiveData = false,
  compactMode = false
}) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  const [startDate, setStartDate] = useState(toDateInputValue(monthStart));
  const [endDate, setEndDate] = useState(toDateInputValue(now));
  const [statusFilter, setStatusFilter] = useState('all');
  const [validationFilter, setValidationFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [centerFilters, setCenterFilters] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportFeedback, setExportFeedback] = useState('');
  const [selectedUsers, setSelectedUsers] = useState({});
  const [expandedUsers, setExpandedUsers] = useState({});
  const [modalState, setModalState] = useState(null);
  const [missingSelections, setMissingSelections] = useState({});
  const [creatingMissing, setCreatingMissing] = useState(false);
  const [missingFeedback, setMissingFeedback] = useState('');
  const [pendingCreatedRecords, setPendingCreatedRecords] = useState([]);
  const [showExportPanel, setShowExportPanel] = useState(() => !compactMode);
  const [showSummaryPanel, setShowSummaryPanel] = useState(true);
  const [showRecordsPanel, setShowRecordsPanel] = useState(() => !compactMode);
  const hasSelectedUsers = useMemo(() => Object.values(selectedUsers).some(Boolean), [selectedUsers]);

  useEffect(() => {
    if (!compactMode) {
      setShowExportPanel(true);
      setShowSummaryPanel(true);
      setShowRecordsPanel(true);
    }
  }, [compactMode]);

  useEffect(() => {
    setExpandedUsers({});
  }, [startDate, endDate, statusFilter, validationFilter, methodFilter, searchTerm, centerFilters]);

  const userById = useMemo(() => {
    const directory = {};
    users.forEach(user => {
      if (!user?.id) return;
      directory[user.id] = user;
    });
    return directory;
  }, [users]);

  const activeUsers = useMemo(() => users.filter(isActiveUser), [users]);
  const eligibleUsers = useMemo(() => activeUsers.filter(hasQr), [activeUsers]);

  const usersSorted = useMemo(() => {
    return [...eligibleUsers].sort((a, b) => {
      const left = (a.clave || a.nombre || '').toLowerCase();
      const right = (b.clave || b.nombre || '').toLowerCase();
      return left.localeCompare(right);
    });
  }, [eligibleUsers]);

  const centerOptions = useMemo(() => {
    const options = new Set();
    eligibleUsers.forEach(user => {
      if (user?.nombreCentroPatron) {
        options.add(user.nombreCentroPatron);
      }
    });
    const ordered = [
      'CUU AC SERVICIOS 2020 SC',
      'LUZ ELENA NAJERA SANCHEZ',
      'ARGELIA FERNANDEZ DE HARO'
    ];
    const orderedOptions = ordered.filter(option => options.has(option));
    const remaining = Array.from(options).filter(option => !ordered.includes(option));
    return [...orderedOptions, ...remaining.sort((a, b) => a.localeCompare(b))];
  }, [eligibleUsers]);

  const normalizedCenterFilters = useMemo(() => {
    return new Set(centerFilters.map(item => toClassName(item)));
  }, [centerFilters]);

  const usersForExport = useMemo(() => {
    if (!normalizedCenterFilters.size) return usersSorted;
    return usersSorted.filter(user =>
      normalizedCenterFilters.has(toClassName(user?.nombreCentroPatron || ''))
    );
  }, [usersSorted, normalizedCenterFilters]);

  const availableTemplateFiles = useMemo(() => new Set(attendanceTemplateFiles), []);

  const userTemplateByClave = useMemo(() => {
    const mapping = {};
    userTemplates.forEach(item => {
      if (!item?.userClave || !item.fileName) return;
      mapping[item.userClave] = item.fileName;
    });
    return mapping;
  }, [userTemplates]);

  const fallbackTemplateByClave = useMemo(() => {
    const mapping = {};
    attendanceUserFallbackTemplates.forEach(item => {
      if (!item?.userClave || !item.fileName) return;
      mapping[item.userClave] = item.fileName;
    });
    return mapping;
  }, []);

  const recordsWithPending = useMemo(() => {
    if (!pendingCreatedRecords.length) return records;
    const existingKeys = new Set(
      records.map(record => `${record.usuarioId}|${getRecordDate(record)}`)
    );
    const pending = pendingCreatedRecords.filter(record => {
      const key = `${record.usuarioId}|${getRecordDate(record)}`;
      return !existingKeys.has(key);
    });
    return records.concat(pending);
  }, [records, pendingCreatedRecords]);

  const recordsById = useMemo(() => {
    const map = {};
    recordsWithPending.forEach(record => {
      if (!record?.id) return;
      map[record.id] = record;
    });
    return map;
  }, [recordsWithPending]);

  const dateRange = useMemo(() => buildDateRange(startDate, endDate), [startDate, endDate]);
  const dateRangeSet = useMemo(() => new Set(dateRange), [dateRange]);

  const recordsByUser = useMemo(() => {
    const map = {};
    usersSorted.forEach(user => {
      const userRecords = [];
      const linkedIds = Array.isArray(user.asistenciasIds) ? user.asistenciasIds : [];
      if (linkedIds.length) {
        linkedIds.forEach(id => {
          const record = recordsById[id];
          if (!record) return;
          const recordDate = getRecordDate(record);
          if (!recordDate || (dateRangeSet.size && !dateRangeSet.has(recordDate))) return;
          userRecords.push(record);
        });
      } else {
        recordsWithPending.forEach(record => {
          if (record.usuarioId !== user.id) return;
          const recordDate = getRecordDate(record);
          if (!recordDate || (dateRangeSet.size && !dateRangeSet.has(recordDate))) return;
          userRecords.push(record);
        });
      }
      map[user.id] = userRecords;
    });
    return map;
  }, [usersSorted, recordsById, recordsWithPending, dateRangeSet]);

  const missingDatesByUser = useMemo(() => {
    const missing = {};
    if (!dateRange.length) return missing;
    usersSorted.forEach(user => {
      const recordsForUser = recordsByUser[user.id] || [];
      const existingDates = new Set(recordsForUser.map(item => getRecordDate(item)).filter(Boolean));
      missing[user.id] = dateRange.filter(date => !existingDates.has(date));
    });
    return missing;
  }, [usersSorted, recordsByUser, dateRange]);

  const filteredRows = useMemo(() => {
    const from = startDate ? new Date(`${startDate}T00:00:00`) : null;
    const to = endDate ? new Date(`${endDate}T23:59:59`) : null;
    const query = searchTerm.trim().toLowerCase();

      return recordsWithPending.filter(record => {
        const user = userById[record.usuarioId] || {};
        const recordDate = getRecordDate(record);
        const parsedRecordDate = recordDate ? new Date(`${recordDate}T12:00:00`) : null;
        if (from && parsedRecordDate && parsedRecordDate < from) return false;
        if (to && parsedRecordDate && parsedRecordDate > to) return false;
        if (statusFilter !== 'all' && record.estado !== statusFilter) return false;
        if (validationFilter !== 'all' && record.validacion !== validationFilter) return false;
        if (methodFilter !== 'all' && record.metodo !== methodFilter) return false;
        if (normalizedCenterFilters.size) {
          const centerText = toClassName(user?.nombreCentroPatron || '');
          if (!normalizedCenterFilters.has(centerText)) return false;
        }
        if (!query) return true;

        return [user.clave, user.nombre, user.iniciales, user.correo, record.estado, record.metodo, record.validacion]
          .map(value => String(value || '').toLowerCase())
          .some(value => value.includes(query));
    });
  }, [
    recordsWithPending,
    userById,
    startDate,
    endDate,
    statusFilter,
    validationFilter,
    methodFilter,
    searchTerm,
    normalizedCenterFilters
  ]);

  const summary = useMemo(() => {
    const total = filteredRows.length;
    const cerradas = filteredRows.filter(item => item.estado === 'Cerrada').length;
    const incompletas = filteredRows.filter(item => item.estado === 'Incompleta').length;
    const fueraHorario = filteredRows.filter(item => item.validacion === 'Fuera de horario').length;
    return { total, cerradas, incompletas, fueraHorario };
  }, [filteredRows]);

  const groupedRows = useMemo(() => {
    const groups = {};
    filteredRows.forEach(record => {
      const userId = record.usuarioId || 'unknown';
      if (!groups[userId]) groups[userId] = [];
      groups[userId].push(record);
    });
    const sortedUserIds = Object.keys(groups).sort((a, b) => {
      const userA = userById[a] || {};
      const userB = userById[b] || {};
      const left = (userA.clave || userA.nombre || '').toLowerCase();
      const right = (userB.clave || userB.nombre || '').toLowerCase();
      return left.localeCompare(right);
    });
    return sortedUserIds.map(userId => {
      const records = [...groups[userId]].sort((a, b) => {
        const dateA = getRecordDate(a) || '';
        const dateB = getRecordDate(b) || '';
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        const timeA = a.inicio || a.fin || '';
        const timeB = b.inicio || b.fin || '';
        return timeA.localeCompare(timeB);
      });
      return {
        userId,
        user: userById[userId] || {},
        records
      };
    });
  }, [filteredRows, userById]);

  const modalPendingUserIds = useMemo(() => {
    if (!modalState) return [];
    return modalState.pendingUserIds.filter(userId => (missingDatesByUser[userId] || []).length > 0);
  }, [modalState, missingDatesByUser]);

  const currentModalUserId = modalState?.userId || '';
  const currentModalUser = currentModalUserId ? userById[currentModalUserId] : null;
  const currentMissingDates = currentModalUserId ? (missingDatesByUser[currentModalUserId] || []) : [];
  const hasMorePendingUsers = useMemo(() => {
    if (!modalState?.bulkUserIds?.length) return false;
    return modalState.bulkUserIds.some(
      userId => userId !== currentModalUserId && (missingDatesByUser[userId] || []).length > 0
    );
  }, [modalState, currentModalUserId, missingDatesByUser]);

  const currentRecordsByDate = useMemo(() => {
    if (!currentModalUserId) return {};
    const recordsForUser = recordsByUser[currentModalUserId] || [];
    return recordsForUser.reduce((acc, record) => {
      const recordDate = getRecordDate(record);
      if (!recordDate) return acc;
      if (!acc[recordDate]) acc[recordDate] = [];
      acc[recordDate].push(record);
      return acc;
    }, {});
  }, [currentModalUserId, recordsByUser]);

  const handleToggleUser = userId => {
    setSelectedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const resolveTemplateFile = user => {
    const userKey = user?.clave;
    const directTemplate = userTemplateByClave[userKey];
    if (directTemplate && availableTemplateFiles.has(directTemplate)) return directTemplate;

    const fallbackTemplate = fallbackTemplateByClave[userKey];
    if (fallbackTemplate && availableTemplateFiles.has(fallbackTemplate)) return fallbackTemplate;

    const preferredDefault = defaultTemplateFile || DEFAULT_ATTENDANCE_TEMPLATE_FILE;
    if (preferredDefault && availableTemplateFiles.has(preferredDefault)) return preferredDefault;

    const firstAvailable = templates.find(item => availableTemplateFiles.has(item.fileName));
    if (firstAvailable) return firstAvailable.fileName;

    return '';
  };

  const buildWorkbookForUser = async (user, userRecords) => {
    const templateFile = resolveTemplateFile(user);
    logDevXlsx('Iniciando construccion de workbook por usuario', {
      userId: user?.id,
      userClave: user?.clave,
      templateFile,
      records: userRecords?.length || 0
    });
    if (!templateFile) {
      throw new Error('No hay plantilla configurada para generar el reporte.');
    }
    if (!availableTemplateFiles.has(templateFile)) {
      throw new Error(`La plantilla "${templateFile}" no existe en asistencias-template.`);
    }
    const templateUrls = buildTemplateUrls(templateFile);
    let response = null;
    let templateStatusCode = 0;
    let templateFetchError = null;
    for (const templateUrl of templateUrls) {
      try {
        logDevXlsx('Descargando plantilla', { templateUrl });
        const candidateResponse = await fetch(templateUrl);
        if (candidateResponse.ok) {
          response = candidateResponse;
          break;
        }
        templateStatusCode = candidateResponse.status;
      } catch (error) {
        templateFetchError = error;
      }
    }
    if (!response) {
      if (templateFetchError && !templateStatusCode) {
        throw new Error('No se pudo abrir la plantilla (sin conexión).');
      }
      throw new Error(`No se pudo abrir la plantilla (${templateStatusCode || 404}).`);
    }
    const buffer = await response.arrayBuffer();
    logDevXlsx('Cargando workbook en memoria', { bytes: buffer.byteLength });
    const workbook = await loadTemplateWorkbook(buffer);
    logDevXlsx('Workbook cargado', {
      sheetCount: workbook.worksheets?.length || 0,
      sheetNames: workbook.worksheets?.map(sheet => sheet?.name)
    });
    const worksheet = workbook.getWorksheet(1) || workbook.worksheets[0];
    if (!worksheet) {
      throw new Error('La plantilla no contiene hojas de calculo.');
    }
    logDevXlsx('Hoja seleccionada para escritura', {
      name: worksheet.name,
      rowCount: worksheet.rowCount,
      columnCount: worksheet.columnCount
    });
    const getWorkbookImageAsset = async fileName => {
      const imageUrl = buildAttendanceImageUrl(fileName);
      if (!imageUrl) return null;
      if (attendanceImageAssetCache.has(imageUrl)) {
        return attendanceImageAssetCache.get(imageUrl);
      }
      try {
        const image = await fetchWorkbookImage(imageUrl);
        attendanceImageAssetCache.set(imageUrl, image);
        return image;
      } catch (error) {
        console.warn('[XLSX] unable to load workbook image', { imageUrl, error });
        attendanceImageAssetCache.set(imageUrl, null);
        return null;
      }
    };

    const userImageFile = resolveUserSignatureImage(user);
    const centerImageFile = resolveCenterSignatureImage(user);
    const [userImageAsset, centerImageAsset] = await Promise.all([
      getWorkbookImageAsset(userImageFile),
      getWorkbookImageAsset(centerImageFile)
    ]);
    const userImageProfile = getImageAspectProfile(userImageAsset?.width, userImageAsset?.height);
    const centerImageProfile = getImageAspectProfile(centerImageAsset?.width, centerImageAsset?.height);
    const userImageId = userImageAsset
      ? workbook.addImage({
        base64: userImageAsset.base64,
        extension: userImageAsset.extension
      })
      : null;
    const centerImageId = centerImageAsset
      ? workbook.addImage({
        base64: centerImageAsset.base64,
        extension: centerImageAsset.extension
      })
      : null;

    const templateRows = buildTemplateRows(userRecords, userById);
    const baseRowIndex = 6;
    const baseRow = worksheet.getRow(baseRowIndex);
    const baseHeight = baseRow?.height || 27;

    const rowsToRender = templateRows.length
      ? templateRows
      : [{ values: new Array(11).fill(null), selfieAttachment: null }];

    for (let offset = 0; offset < rowsToRender.length; offset += 1) {
      const rowNumber = baseRowIndex + offset;
      const row = worksheet.getRow(rowNumber);
      row.height = baseHeight;

      if (offset > 0) {
        baseRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          const target = row.getCell(colNumber);
          target.style = { ...cell.style };
          if (cell.numFmt) target.numFmt = cell.numFmt;
        });
      }

      applyRowValues(row, rowsToRender[offset].values);

      const signatureRange = `J${rowNumber}:K${rowNumber}`;
      if (!row.getCell(10).isMerged) {
        try {
          worksheet.mergeCells(signatureRange);
        } catch (error) {
          // Ignore merge conflicts for templates that already include this merge.
        }
      }
      [row.getCell(10), row.getCell(11)].forEach(cell => {
        cell.border = {
          ...(cell.border || {}),
          bottom: { style: 'thin', color: { argb: 'FF000000' } }
        };
      });

    }

    const centroPatronValue = normalizeUserValue(user?.nombreCentroPatron);
    const comercialPatronValue = normalizeUserValue(user?.nombreComercialPatron);
    const headerParts = [
      `Nombre centro y/o Patrón: ${centroPatronValue || ''}`.trim(),
      `Nombre comercial: ${comercialPatronValue || ''}`.trim()
    ];
    const userLabel = normalizeUserValue(user?.nombre || user?.clave);
    if (userLabel) {
      headerParts.push(`Usuario: ${userLabel}`);
    }
    const headerLine = headerParts.join(' | ').trim();
    worksheet.getCell('A2').value = null;
    worksheet.getCell('B2').value = null;
    worksheet.getCell('F2').value = null;
    const headerRow = worksheet.getRow(2);
    if (headerRow) {
      headerRow.hidden = false;
      headerRow.height = headerRow.height || 22;
    }

    const tutorValue = normalizeUserValue(user?.tutor);
    const folioCentroValue = normalizeUserValue(user?.folioCentro);
    const showProgramTitle = Boolean(tutorValue && folioCentroValue);

    worksheet.getCell('A1').value = showProgramTitle ? PROGRAM_TITLE : null;
    const reportLine = `Reporte de Asistencia del ${startDate} al ${endDate}`;
    worksheet.getCell('A3').value = headerLine ? `${headerLine}\n${reportLine}` : reportLine;
    const reportRow = worksheet.getRow(3);
    if (reportRow) {
      reportRow.hidden = false;
      if (headerLine) {
        reportRow.height = Math.max(reportRow.height || 0, 54);
      }
    }
    const reportCell = worksheet.getCell('A3');
    if (reportCell) {
      reportCell.alignment = { ...(reportCell.alignment || {}), wrapText: true };
    }

    worksheet.getCell('A4').value = tutorValue ? `Tutor: ${tutorValue}` : null;
    worksheet.getCell('E4').value = folioCentroValue ? `Folio: ${folioCentroValue}` : null;
    const tutorCell = worksheet.getCell('A4');
    if (tutorCell) {
      tutorCell.alignment = { ...(tutorCell.alignment || {}), wrapText: true, vertical: 'middle' };
      tutorCell.font = { ...(tutorCell.font || {}), size: 10 };
    }
    const folioCell = worksheet.getCell('E4');
    if (folioCell) {
      folioCell.alignment = { ...(folioCell.alignment || {}), vertical: 'middle' };
      folioCell.font = { ...(folioCell.font || {}), size: 10 };
    }
    const tutorRow = worksheet.getRow(4);
    if (tutorRow) {
      const minHeight = userImageId ? getUserHeaderImageRowHeight(userImageProfile, userImageFile) : 24;
      tutorRow.height = Math.max(tutorRow.height || 0, minHeight);
    }

    if (userImageId) {
      placeImageInRange({
        worksheet,
        imageId: userImageId,
        image: userImageAsset,
        startCol: 7,
        endCol: 9,
        startRow: 4,
        endRow: 4,
        ...getUserHeaderImageLimits(userImageProfile, userImageFile)
      });
    }

    if (centerImageId) {
      placeImageInRange({
        worksheet,
        imageId: centerImageId,
        image: centerImageAsset,
        startCol: 10,
        endCol: 11,
        startRow: 1,
        endRow: 4,
        ...getCenterImageLimits(centerImageProfile, centerImageFile)
      });
    }

    const imageCache = new Map();
    for (let index = 0; index < rowsToRender.length; index += 1) {
      const attachment = rowsToRender[index].selfieAttachment;
      if (!attachment) continue;
      const url = getSelfieUrl(attachment);
      if (!url) continue;
      let imageId = imageCache.get(url);
      if (!imageId) {
        try {
          const base64 = await fetchImageBase64(url);
          imageId = workbook.addImage({
            base64,
            extension: getImageExtension(attachment, url)
          });
          imageCache.set(url, imageId);
        } catch (error) {
          continue;
        }
      }
      const rowNumber = baseRowIndex + index;
      worksheet.addImage(imageId, {
        tl: { col: 8, row: rowNumber - 1 },
        ext: { width: 32, height: 32 }
      });
    }

    const fileKey = sanitizeFilename(user?.clave || user?.iniciales || user?.id || 'usuario');
    const outName = `Reporte_Asistencia_${fileKey}_${startDate}_${endDate}.xlsx`;
    return { workbook, outName };
  };

  const openMissingModal = ({ userId, pendingUserIds, mode, bulkUserIds }) => {
    setModalState({
      userId,
      pendingUserIds,
      mode,
      bulkUserIds
    });
    setMissingFeedback('');
  };

  const closeMissingModal = () => {
    setModalState(null);
    setMissingFeedback('');
    setMissingSelections({});
  };

  const handleExportUser = async userId => {
    const missingDates = missingDatesByUser[userId] || [];
    if (missingDates.length) {
      openMissingModal({ userId, pendingUserIds: [userId], mode: 'single', bulkUserIds: [userId] });
      return;
    }
    try {
      setExporting(true);
      setExportFeedback('');
      const user = userById[userId];
    const userRecords = recordsByUser[userId] || [];
      const { workbook, outName } = await buildWorkbookForUser(user, userRecords);
      await downloadWorkbook(workbook, outName);
      setExportFeedback(`Reporte generado correctamente (${outName}).`);
    } catch (exportError) {
      setExportFeedback(exportError.message || 'No fue posible generar el archivo XLSX.');
    } finally {
      setExporting(false);
    }
  };

  const exportUserIdsAsZip = async userIds => {
    const JSZip = await loadJsZipLibrary();
    const zip = new JSZip();

    for (const userId of userIds) {
      const user = userById[userId];
      const userRecords = recordsByUser[userId] || [];
      const { workbook, outName } = await buildWorkbookForUser(user, userRecords);
    const buffer = await workbook.xlsx.writeBuffer({ useStyles: true, useSharedStrings: true });
      zip.file(outName, buffer);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const zipName = `Reportes_Asistencia_${startDate}_${endDate}.zip`;
    downloadBlob(zipBlob, zipName);
    setExportFeedback(`ZIP generado correctamente (${zipName}).`);
  };

  const handleExportSelected = async () => {
    const selectedUserIds = usersSorted.filter(user => selectedUsers[user.id]).map(user => user.id);
    if (!selectedUserIds.length) {
      setExportFeedback('Selecciona al menos un usuario para exportar.');
      return;
    }
    const missingUserIds = selectedUserIds.filter(userId => (missingDatesByUser[userId] || []).length > 0);
    if (missingUserIds.length) {
      openMissingModal({
        userId: missingUserIds[0],
        pendingUserIds: missingUserIds,
        mode: 'bulk',
        bulkUserIds: selectedUserIds
      });
      return;
    }
    try {
      setExporting(true);
      setExportFeedback('');
      await exportUserIdsAsZip(selectedUserIds);
    } catch (exportError) {
      setExportFeedback(exportError.message || 'No fue posible generar el ZIP.');
    } finally {
      setExporting(false);
    }
  };

  const handleCreateMissingRecord = async date => {
    if (!currentModalUserId) return;
    if (!asistenciasTableId) {
      setMissingFeedback('No se ha configurado la tabla de asistencias.');
      return;
    }
    const selectionKey = `${currentModalUserId}:${date}`;
    const selectedType = missingSelections[selectionKey] || 'Asistencia';

    try {
      setCreatingMissing(true);
      setMissingFeedback('');
      await createAirtableRecord({
        tableId: asistenciasTableId,
        fields: {
          [ASISTENCIAS_FIELD_IDS.usuario]: [currentModalUserId],
          [ASISTENCIAS_FIELD_IDS.fecha]: date,
          [ASISTENCIAS_FIELD_IDS.tipo]: selectedType
        }
      });

      setPendingCreatedRecords(prev => [
        ...prev,
        {
          id: `pending-${currentModalUserId}-${date}`,
          usuarioId: currentModalUserId,
          fecha: date,
          inicio: null,
          fin: null,
          estado: '',
          metodo: '',
          validacion: '',
          duracionMin: null,
          tipo: selectedType
        }
      ]);

      if (onRefresh) onRefresh();
      setMissingFeedback(`Registro creado para ${date}.`);
    } catch (createError) {
      setMissingFeedback(createError.message || 'No fue posible crear el registro.');
    } finally {
      setCreatingMissing(false);
    }
  };

  const handleContinueAfterMissing = async () => {
    if (!modalState) return;
    const remainingMissing = modalState.bulkUserIds.filter(userId => (missingDatesByUser[userId] || []).length > 0);
    if (remainingMissing.length) {
      setModalState(prev => ({
        ...prev,
        userId: remainingMissing[0],
        pendingUserIds: remainingMissing
      }));
      return;
    }

    closeMissingModal();
    if (modalState.mode === 'bulk') {
      try {
        setExporting(true);
        setExportFeedback('');
        await exportUserIdsAsZip(modalState.bulkUserIds);
      } catch (exportError) {
        setExportFeedback(exportError.message || 'No fue posible generar el ZIP.');
      } finally {
        setExporting(false);
      }
      return;
    }

    if (modalState.mode === 'single' && modalState.bulkUserIds?.[0]) {
      await handleExportUser(modalState.bulkUserIds[0]);
    }
  };

  const handleToggleSelectAll = () => {
    if (hasSelectedUsers) {
      setSelectedUsers({});
      return;
    }
    if (!usersForExport.length) return;
    setSelectedUsers(prev => {
      const next = { ...prev };
      usersForExport.forEach(user => {
        next[user.id] = true;
      });
      return next;
    });
  };

  const handleToggleGroup = userId => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const handleToggleCenterFilter = option => {
    setCenterFilters(prev => {
      const normalized = toClassName(option);
      if (prev.some(item => toClassName(item) === normalized)) {
        return prev.filter(item => toClassName(item) !== normalized);
      }
      return [...prev, option];
    });
  };

  const handleClearCenterFilters = () => {
    setCenterFilters([]);
  };

  return (
    <section className="attendance-view">
      <header className="attendance-header">
        <div>
          <p className="eyebrow">Sección nueva</p>
          <h2>Reportes de asistencias</h2>
          <p>Genera reportes por usuario con validación de días faltantes y exportación en ZIP.</p>
        </div>
      </header>

      {error && <p className="helper-text error">{error}</p>}
      {!error && (
        <p className={`helper-text ${usingLiveData ? 'success' : 'warning'}`}>
          {usingLiveData ? 'Datos cargados desde Airtable.' : 'Mostrando datos de ejemplo (sin conexion Airtable).'}
        </p>
      )}
      {exportFeedback && <p className="helper-text">{exportFeedback}</p>}

      <div className="table-filters attendance-filters">
        <label className="filter-control">
          <span>Desde</span>
          <input type="date" value={startDate} onChange={event => setStartDate(event.target.value)} />
        </label>
        <label className="filter-control">
          <span>Hasta</span>
          <input type="date" value={endDate} onChange={event => setEndDate(event.target.value)} />
        </label>
        <label className="filter-control">
          <span>Estado</span>
          <select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
            {STATUS_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option === 'all' ? 'Todos' : option}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-control">
          <span>Validación</span>
          <select value={validationFilter} onChange={event => setValidationFilter(event.target.value)}>
            {VALIDATION_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option === 'all' ? 'Todas' : option}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-control">
          <span>Método</span>
          <select value={methodFilter} onChange={event => setMethodFilter(event.target.value)}>
            {METHOD_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option === 'all' ? 'Todos' : option}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-control search-control">
          <span>Buscar usuario</span>
          <input
            type="search"
            value={searchTerm}
            placeholder="Nombre, iniciales o correo"
            onChange={event => setSearchTerm(event.target.value)}
          />
        </label>
        <div className="filter-control center-filter-group">
          <span>Nombre centro/patrón</span>
          <div className="center-filter-options">
            <button
              type="button"
              className={`center-chip ${!centerFilters.length ? 'active' : ''}`}
              onClick={handleClearCenterFilters}
            >
              Todos
            </button>
            {centerOptions.map(option => {
              const active = centerFilters.some(item => toClassName(item) === toClassName(option));
              return (
                <button
                  key={option}
                  type="button"
                  className={`center-chip center-${toClassName(option)} ${active ? 'active' : ''}`}
                  onClick={() => handleToggleCenterFilter(option)}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <button
        type="button"
        className="attendance-section-toggle"
        aria-expanded={showExportPanel}
        onClick={() => setShowExportPanel(prev => !prev)}
      >
        Usuarios para exportar
        <span>{showExportPanel ? '▾' : '▸'}</span>
      </button>
      <div className="attendance-section-panel" hidden={!showExportPanel}>
        <div className="attendance-export-panel">
          <div className="attendance-export-header">
            <div>
              <h3>Usuarios para exportar</h3>
              <p>Marca los usuarios que deseas exportar y completa los días faltantes del periodo.</p>
            </div>
            <div className="attendance-export-actions">
              <button
                type="button"
                className="btn ghost"
                onClick={handleToggleSelectAll}
                disabled={!usersForExport.length && !hasSelectedUsers}
              >
                {hasSelectedUsers ? 'Limpiar selección' : 'Seleccionar todo'}
              </button>
              <button type="button" className="btn" onClick={handleExportSelected} disabled={exporting || loading}>
                {exporting ? 'Generando...' : 'Exportar seleccionados'}
              </button>
            </div>
          </div>
          <div className="attendance-export-list">
            {!usersForExport.length && <p className="helper-text">No hay usuarios disponibles para exportar.</p>}
            {usersForExport.map(user => {
              const missingDates = missingDatesByUser[user.id] || [];
              const isReady = missingDates.length === 0;
              const isChecked = Boolean(selectedUsers[user.id]);
              const statusLabel = isReady ? 'Listo' : `${missingDates.length} dia(s) faltante(s)`;
              return (
                <article key={user.id} className={`attendance-export-item ${isReady ? 'ready' : 'missing'}`}>
                  <label className="attendance-export-check">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleToggleUser(user.id)}
                      aria-label={`Seleccionar ${user.nombre || 'usuario'}`}
                    />
                  </label>
                  <div className="attendance-export-info">
                    <strong>{user.nombre || 'Usuario'}</strong>
                    <span>{user.clave || user.iniciales || user.correo || 'Sin clave'}</span>
                  </div>
                  <span className={`attendance-export-status ${isReady ? 'ready' : 'missing'}`}>{statusLabel}</span>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => handleExportUser(user.id)}
                    disabled={exporting || loading}
                  >
                    Exportar
                  </button>
                </article>
              );
            })}
          </div>
        </div>
      </div>

      <button
        type="button"
        className="attendance-section-toggle"
        aria-expanded={showSummaryPanel}
        onClick={() => setShowSummaryPanel(prev => !prev)}
      >
        Resumen del periodo
        <span>{showSummaryPanel ? '▾' : '▸'}</span>
      </button>
      <div className="attendance-section-panel" hidden={!showSummaryPanel}>
        <div className="attendance-summary-grid">
          <article className="attendance-summary-card">
            <p className="label">Registros</p>
            <h3>{summary.total}</h3>
          </article>
          <article className="attendance-summary-card">
            <p className="label">Cerradas</p>
            <h3>{summary.cerradas}</h3>
          </article>
          <article className="attendance-summary-card">
            <p className="label">Incompletas</p>
            <h3>{summary.incompletas}</h3>
          </article>
          <article className="attendance-summary-card">
            <p className="label">Fuera de horario</p>
            <h3>{summary.fueraHorario}</h3>
          </article>
        </div>
      </div>

      <button
        type="button"
        className="attendance-section-toggle"
        aria-expanded={showRecordsPanel}
        onClick={() => setShowRecordsPanel(prev => !prev)}
      >
        Registros detallados
        <span>{showRecordsPanel ? '▾' : '▸'}</span>
      </button>
      <div className="attendance-section-panel" hidden={!showRecordsPanel}>
        <div className="attendance-table-wrap">
          <div className="attendance-table">
            <div className="attendance-table-head">
              <span>Usuario</span>
              <span>Fecha</span>
              <span>Inicio</span>
              <span>Fin</span>
              <span>Estado</span>
              <span>Validación</span>
              <span>Duracion</span>
              <span>Método</span>
            </div>
            {!groupedRows.length && <p className="helper-text">No hay asistencias con los filtros aplicados.</p>}
            {groupedRows.map(group => {
              const user = group.user || {};
              const isExpanded = Boolean(expandedUsers[group.userId]);
              const firstDate = group.records.length ? formatDate(getRecordDate(group.records[0])) : '';
              const lastDate = group.records.length
                ? formatDate(getRecordDate(group.records[group.records.length - 1]))
                : '';
              return (
                <div key={group.userId} className="attendance-group">
                  <div className="attendance-group-header">
                    <button
                      type="button"
                      className="attendance-group-toggle"
                      onClick={() => handleToggleGroup(group.userId)}
                      aria-expanded={isExpanded}
                      aria-label={`${
                        isExpanded ? 'Colapsar' : 'Expandir'
                      } asistencias de ${user.nombre || 'usuario'}`}
                    >
                      {isExpanded ? '▾' : '▸'}
                    </button>
                    <div className="attendance-group-info">
                      <strong>{user.nombre || 'Usuario'}</strong>
                      <small>{user.clave || user.iniciales || 'Sin clave'}</small>
                    </div>
                    <div className="attendance-group-meta">
                      <span>{group.records.length} registro(s)</span>
                      {firstDate && lastDate && (
                        <span>
                          {firstDate}
                          {firstDate !== lastDate ? ` · ${lastDate}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  {isExpanded &&
                    group.records.map(record => (
                      <article key={record.id} className="attendance-table-row">
                        <div>
                          <strong>{user.nombre || 'Usuario'}</strong>
                          <small>{user.clave || user.iniciales || 'Sin clave'}</small>
                        </div>
                        <span>{formatDate(getRecordDate(record))}</span>
                        <span>{formatTime(record.inicio)}</span>
                        <span>{formatTime(record.fin)}</span>
                        <span className={`attendance-chip state ${toClassName(record.estado)}`}>
                          {record.estado || '---'}
                        </span>
                        <span className={`attendance-chip validation ${toClassName(record.validacion)}`}>
                          {record.validacion || '---'}
                        </span>
                        <span>{formatDuration(record.duracionMin, record.inicio, record.fin)}</span>
                        <span>{record.metodo || '---'}</span>
                      </article>
                    ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {modalState && (
        <div
          className="overlay attendance-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Validar días faltantes"
          onClick={closeMissingModal}
        >
          <div className="attendance-missing-modal" onClick={event => event.stopPropagation()}>
            <div className="attendance-modal-badge">{modalPendingUserIds.length}</div>
            <div className="modal-header">
              <div>
                <h3>Validar días faltantes</h3>
                <p>
                  {currentModalUser?.nombre || 'Usuario'} ({currentModalUser?.clave || 'Sin clave'})
                </p>
              </div>
              <button type="button" className="link-btn" onClick={closeMissingModal}>
                Cerrar
              </button>
            </div>
            <p className="helper-text">
              Periodo seleccionado: {startDate} al {endDate}. Selecciona el tipo para crear los registros faltantes.
            </p>
            {missingFeedback && <p className="helper-text">{missingFeedback}</p>}

            <div className="attendance-missing-list">
              {dateRange.map(date => {
                const isMissing = currentMissingDates.includes(date);
                const recordList = currentRecordsByDate[date] || [];
                const recordSummary = recordList.length
                  ? recordList.length === 1
                    ? recordList[0].tipo || recordList[0].estado || 'Registro'
                    : `${recordList.length} registros`
                  : '';
                const selectionKey = `${currentModalUserId}:${date}`;
                const selectedType = missingSelections[selectionKey] || 'Asistencia';

                return (
                  <div key={date} className={`attendance-missing-row ${isMissing ? 'missing' : 'complete'}`}>
                    <div>
                      <strong>{formatDate(date)}</strong>
                      <small>{date}</small>
                    </div>
                    {isMissing ? (
                      <div className="attendance-missing-actions">
                        <select
                          value={selectedType}
                          onChange={event =>
                            setMissingSelections(prev => ({
                              ...prev,
                              [selectionKey]: event.target.value
                            }))
                          }
                        >
                          {TYPE_OPTIONS.map(option => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn ghost"
                          onClick={() => handleCreateMissingRecord(date)}
                          disabled={creatingMissing}
                        >
                          {creatingMissing ? 'Creando...' : 'Crear registro'}
                        </button>
                      </div>
                    ) : (
                      <span className="attendance-missing-status">
                        Registrado{recordSummary ? ` · ${recordSummary}` : ''}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="attendance-modal-footer">
              <div>
                {currentMissingDates.length
                  ? `Faltan ${currentMissingDates.length} dia(s) por completar.`
                  : 'Todos los días están completos para este usuario.'}
              </div>
              <button
                type="button"
                className="btn"
                onClick={handleContinueAfterMissing}
                disabled={currentMissingDates.length > 0 || exporting}
              >
                {hasMorePendingUsers
                  ? 'Siguiente usuario'
                  : modalState.mode === 'single'
                    ? 'Descargar reporte'
                    : 'Descargar reportes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default AttendanceReportsBoard;
